import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { CalendarDays, MapPin, Clock as ClockIcon, Swords } from "lucide-react";

// --- ANIMACJE ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- STYLED COMPONENTS ---
const CalendarWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

const CalendarHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px; /* Zmniejszyłem lekko, bo dodajemy padding w DaysTrack */
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CalendarTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  text-transform: uppercase;
  margin: 0;
  color: #111;
  letter-spacing: -1px;
  line-height: 1;

  span {
    color: #888;
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 20px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #555;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${(props) => props.bg};
  border: 1px solid ${(props) => props.border || props.bg};
`;

const DaysTrack = styled.div`
  display: flex;
  gap: 15px;
  overflow-x: auto;
  
  /* KLUCZOWA ZMIANA: Dodajemy padding z góry i z dołu, 
     aby karty mogły się bezpiecznie podnieść (translateY) i pokazać cień (box-shadow) 
     bez bycia ucinanymi przez krawędzie kontenera */
  padding-top: 15px;
  padding-bottom: 25px; 
  padding-left: 5px; /* Minimalny padding z boku dla cienia pierwszej karty */
  padding-right: 5px;

  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;

  /* Ukrycie paska przewijania na rzecz czystego designu */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DayCard = styled.div`
  min-width: 80px;
  height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  scroll-snap-align: start;
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  position: relative;
  
  background: ${(props) => 
    props.type === "game" ? "#111" : 
    props.type === "training" ? "#f9f9f9" : "#fff"
  };
  color: ${(props) => 
    props.type === "game" ? "#fff" : 
    props.type === "training" ? "#111" : "#ccc"
  };
  border: 2px solid ${(props) => 
    props.isSelected ? "#111" : 
    props.type === "game" ? "#111" : 
    props.type === "training" ? "#e0e0e0" : "#f0f0f0"
  };
  cursor: ${(props) => (props.type ? "pointer" : "default")};

  /* Wizualny wskaźnik wyboru */
  ${(props) => props.isSelected && `
    transform: translateY(-8px);
    box-shadow: 0 12px 24px rgba(0,0,0,0.15);
  `}

  &:hover {
    ${(props) => props.type && !props.isSelected && `
      transform: translateY(-5px);
      box-shadow: 0 8px 16px rgba(0,0,0,0.1);
      border-color: #111;
    `}
  }
`;

const DayNumber = styled.span`
  font-size: 2.2rem;
  font-weight: 900;
  line-height: 1;
`;

const DayName = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 5px;
  opacity: ${(props) => (props.type === "game" ? "0.8" : "1")};
`;

const DetailsPanel = styled.div`
  margin-top: 10px;
  padding: 30px;
  background: #f9f9f9;
  border-left: 4px solid ${(props) => (props.type === "game" ? "#111" : "#888")};
  animation: ${fadeIn} 0.3s ease-out;
  display: flex;
  flex-direction: column;
  gap: 15px;

  h3 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 900;
    text-transform: uppercase;
    color: #111;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.05rem;
  color: #555;

  svg {
    width: 20px;
    height: 20px;
    color: #111;
  }
  
  strong {
    color: #111;
  }
`;

// --- STAŁE ---
const weekdayPl = ["Ndz", "Pon", "Wt", "Śr", "Czw", "Pt", "Sob"];

// --- KOMPONENT GŁÓWNY ---
export const UpcomingCalendar = ({ startDate = null, eventsDefinitions = [], rocznikId }) => {
    const [matches, setMatches] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);

    // --- Pobieranie meczów z API ---
    useEffect(() => {
        if (!rocznikId) return;
        const fetchMatches = async () => {
            try {
                const res = await fetch(`https://api.pth-hokej.pl/api/matches/rocznik/${rocznikId}`);
                if (!res.ok) throw new Error("Błąd pobierania meczów");
                const data = await res.json();
                setMatches(data);
            } catch (err) {
                console.error("Błąd API (Mecze):", err);
            }
        };
        fetchMatches();
    }, [rocznikId]);

    // --- Generowanie kalendarza ---
    const start = startDate
        ? new Date(startDate.split(".")[2], parseInt(startDate.split(".")[1], 10) - 1, startDate.split(".")[0])
        : new Date();

    const days = [];
    let dateCursor = new Date(start);

    for (let i = 0; i < 14; i++) {
        const dayOfWeek = dateCursor.getDay();
        const pad = (n) => (n < 10 ? "0" + n : n);
        const isoDate = `${dateCursor.getFullYear()}-${pad(dateCursor.getMonth() + 1)}-${pad(dateCursor.getDate())}`;

        const isTrainingDay = eventsDefinitions.includes(dayOfWeek);
        const matchForDay = matches.find(m => m.date === isoDate);

        days.push({
            key: isoDate,
            nr: pad(dateCursor.getDate()),
            dow: weekdayPl[dayOfWeek],
            type: matchForDay ? "game" : isTrainingDay ? "training" : null,
            match: matchForDay || null,
            fullDate: isoDate
        });

        dateCursor.setDate(dateCursor.getDate() + 1);
    }

    // --- Obsługa kliknięcia ---
    const handleDayClick = (day) => {
        if (!day.type) {
            setSelectedDay(null);
            return;
        }

        if (selectedDay && selectedDay.date === day.fullDate) {
            setSelectedDay(null);
            return;
        }

        setSelectedDay({
            name: day.type === "game" ? "Mecz Ligowy" : "Trening Drużyny",
            date: day.fullDate,
            opponent: day.match?.opponentName || "",
            address: day.match?.address || (day.type === "training" ? "Główna Baza Treningowa" : ""),
            time: day.match?.time || "Wg. harmonogramu",
            type: day.type
        });
    };

    return (
        <CalendarWrapper>
            {/* --- NAGŁÓWEK --- */}
            <CalendarHeaderRow>
                <CalendarTitle>
                    Z kalendarza <span>drużyny</span>
                </CalendarTitle>
                <Legend>
                    <LegendItem>
                        <LegendColor bg="#111" /> Mecz
                    </LegendItem>
                    <LegendItem>
                        <LegendColor bg="#f9f9f9" border="#ccc" /> Trening
                    </LegendItem>
                </Legend>
            </CalendarHeaderRow>

            {/* --- PASEK Z DNIAMI --- */}
            <DaysTrack>
                {days.map((day) => (
                    <DayCard
                        key={day.key}
                        type={day.type}
                        isSelected={selectedDay?.date === day.fullDate}
                        onClick={() => handleDayClick(day)}
                    >
                        <DayNumber>{day.nr}</DayNumber>
                        <DayName type={day.type}>{day.dow}</DayName>
                    </DayCard>
                ))}
            </DaysTrack>

            {/* --- KARTA SZCZEGÓŁÓW --- */}
            {selectedDay && (
                <DetailsPanel type={selectedDay.type}>
                    <h3>
                        {selectedDay.type === "game" ? <Swords /> : <CalendarDays />} 
                        {selectedDay.name}
                    </h3>
                    
                    <DetailRow>
                        <CalendarDays />
                        <span>Data: <strong>{selectedDay.date}</strong></span>
                    </DetailRow>

                    <DetailRow>
                        <ClockIcon />
                        <span>Godzina: <strong>{selectedDay.time}</strong></span>
                    </DetailRow>

                    {selectedDay.opponent && (
                        <DetailRow>
                            <Swords />
                            <span>Rywal: <strong>{selectedDay.opponent}</strong></span>
                        </DetailRow>
                    )}

                    <DetailRow>
                        <MapPin />
                        <span>Lokalizacja: <strong>{selectedDay.address}</strong></span>
                    </DetailRow>
                </DetailsPanel>
            )}
        </CalendarWrapper>
    );
};